type Env = Record<string, string | boolean | undefined>

// OpenAnalyst URLs:
// Dev, beta, and production all use the live OpenAnalyst endpoint pair.
//   account:    same hosts as Smartwork tenx control plane
// Smartwork tenx control plane (10xKB / auth SSO): ai.openanalyst.com for every channel.
// Do NOT mix OA and Smartwork URLs — OA features go to OA URLs; only the
// auth SSO callback transits Smartwork hosts.
const STAGING_WEB_BASE_URL = "https://web.openanalyst.com"
const PRODUCTION_WEB_BASE_URL = "https://web.openanalyst.com"
const STAGING_ACCOUNT_BASE_URL = "https://ai.openanalyst.com"
const PRODUCTION_ACCOUNT_BASE_URL = "https://ai.openanalyst.com"
const STAGING_BILLING_BASE_URL = "https://api.openanalyst.com"
const PRODUCTION_BILLING_BASE_URL = "https://api.openanalyst.com"
const STAGING_TENX_BASE_URL = "https://ai.openanalyst.com"
const PRODUCTION_TENX_BASE_URL = "https://ai.openanalyst.com"

function currentEnv(): Env {
  return {
    VITE_SMARTWORK_API_BASE_URL: import.meta.env.VITE_SMARTWORK_API_BASE_URL,
    VITE_SMARTWORK_URL: import.meta.env.VITE_SMARTWORK_URL,
    VITE_TENX_AUTH_URL: import.meta.env.VITE_TENX_AUTH_URL,
    VITE_OPENANALYST_ACCOUNT_URL: import.meta.env.VITE_OPENANALYST_ACCOUNT_URL,
    VITE_AUTH_URL: import.meta.env.VITE_AUTH_URL,
    VITE_OPENANALYST_API_URL: import.meta.env.VITE_OPENANALYST_API_URL,
    VITE_BILLING_URL: import.meta.env.VITE_BILLING_URL,
    VITE_MARKETPLACE_URL: import.meta.env.VITE_MARKETPLACE_URL,
  }
}

function envString(env: Env, key: string) {
  const value = env[key]
  return typeof value === "string" ? value.trim() : ""
}

function trimBaseUrl(value: string) {
  return value.trim().replace(/\/+$/, "")
}

function trimOpenAnalystWebBaseUrl(value: string) {
  return trimBaseUrl(value).replace(/\/api\/v1$/, "")
}

function urlHost(value: string) {
  try {
    return new URL(value).hostname.toLowerCase()
  } catch {
    return ""
  }
}

function browserOrigin() {
  if (typeof window === "undefined") return ""
  if (window.location.protocol !== "http:" && window.location.protocol !== "https:") return ""
  return trimBaseUrl(window.location.origin)
}

function hostedAppBrowserOrigin(value: string) {
  const host = urlHost(value)
  if (
    host === "app.openanalyst.com" ||
    host === "app.basera.com" ||
    host === "cloud.openanalyst.com" ||
    host === "smartwork.arjun.tv" ||
    host === "smartwork.openanalyst.com"
  ) {
    return trimBaseUrl(value)
  }
  return ""
}

function smartworkHostedBaseUrl(env: Env) {
  const host = [browserOrigin(), envString(env, "VITE_AUTH_URL"), envString(env, "VITE_OPENANALYST_API_URL")]
    .map(trimOpenAnalystWebBaseUrl)
    .map(urlHost)
    .find(
      (value) =>
        value === "cloud.openanalyst.com" || value === "smartwork.arjun.tv" || value === "smartwork.openanalyst.com",
    )
  if (host === "cloud.openanalyst.com" || host === "smartwork.openanalyst.com") return PRODUCTION_TENX_BASE_URL
  if (host === "smartwork.arjun.tv") return STAGING_TENX_BASE_URL
  return ""
}

function pairedHostedWebHost(host: string) {
  // OpenAnalyst production web/API hosts → pair to production web auth facade
  if (
    host === "web.openanalyst.com" ||
    host === "www.openanalyst.com" ||
    host === "app.openanalyst.com" ||
    host === "api.openanalyst.com"
  ) {
    return "web.openanalyst.com"
  }
  if (host.endsWith(".basera.com")) return "web.openanalyst.com"
  // Smartwork control-plane hosts identify the OA environment.
  if (host === "ai.openanalyst.com") return "web.openanalyst.com"
  return ""
}

function pairedHostedWebBaseUrl(value: string) {
  const paired = pairedHostedWebHost(urlHost(value))
  if (paired) return `https://${paired}`
  return normalizeOpenAnalystTenxBaseUrl(trimOpenAnalystWebBaseUrl(value))
}

export function normalizeOpenAnalystTenxBaseUrl(value: string) {
  return trimBaseUrl(value)
}

function hostedWebHost(env: Env) {
  const configuredRawHost = urlHost(envString(env, "VITE_AUTH_URL") || envString(env, "VITE_OPENANALYST_API_URL"))
  if (
    configuredRawHost === "cloud.openanalyst.com" ||
    configuredRawHost === "smartwork.arjun.tv" ||
    configuredRawHost === "smartwork.openanalyst.com"
  ) {
    return configuredRawHost
  }
  const configuredHost = pairedHostedWebHost(configuredRawHost)
  if (configuredHost) return configuredHost
  const browserRawHost = urlHost(browserOrigin())
  if (
    browserRawHost === "cloud.openanalyst.com" ||
    browserRawHost === "smartwork.arjun.tv" ||
    browserRawHost === "smartwork.openanalyst.com"
  ) {
    return browserRawHost
  }
  const browserHost = pairedHostedWebHost(browserRawHost)
  if (browserHost) return browserHost
  return ""
}

function isProductionHost(env: Env) {
  const host = hostedWebHost(env)
  // OA prod = web.openanalyst.com (paired from app/web/api.openanalyst.com),
  // Hosted Cloud prod = cloud.openanalyst.com; legacy Smartwork prod remains compatible.
  return host === "cloud.openanalyst.com" || host === "web.openanalyst.com" || host === "smartwork.openanalyst.com"
}

function usesProductionBillingHost(env: Env) {
  const hosts = [
    urlHost(envString(env, "VITE_AUTH_URL")),
    urlHost(envString(env, "VITE_OPENANALYST_API_URL")),
    urlHost(browserOrigin()),
  ]
  return (
    hosts.includes("cloud.openanalyst.com") ||
    hosts.includes("smartwork.arjun.tv") ||
    hosts.includes("smartwork.openanalyst.com") ||
    isProductionHost(env)
  )
}

export function openAnalystWebBaseUrl(env: Env = currentEnv()) {
  const explicit = envString(env, "VITE_AUTH_URL") || envString(env, "VITE_OPENANALYST_API_URL")
  if (explicit) return pairedHostedWebBaseUrl(explicit)
  const origin = browserOrigin()
  const host = urlHost(origin)
  // Accept OA web hosts and Smartwork SSO origins as valid OA web bases when running in-browser.
  if (
    host === "app.openanalyst.com" ||
    host === "web.openanalyst.com" ||
    host === "api.openanalyst.com" ||
    host.endsWith(".basera.com") ||
    host === "cloud.openanalyst.com" ||
    host === "smartwork.arjun.tv" ||
    host === "smartwork.openanalyst.com" ||
    host === "ai.openanalyst.com"
  ) {
    return pairedHostedWebBaseUrl(origin)
  }
  return STAGING_WEB_BASE_URL
}

export function openAnalystBrowserAuthBaseUrl(env: Env = currentEnv()) {
  const hostedOrigin = hostedAppBrowserOrigin(browserOrigin())
  if (hostedOrigin) return hostedOrigin
  const explicit = envString(env, "VITE_AUTH_URL") || envString(env, "VITE_OPENANALYST_API_URL")
  if (explicit) return pairedHostedWebBaseUrl(explicit)
  return openAnalystWebBaseUrl(env)
}

export function isOpenAnalystHostedBrowserAuthBaseUrl(value = openAnalystBrowserAuthBaseUrl()) {
  return !!hostedAppBrowserOrigin(value)
}

export function openAnalystTenxBaseUrl(env: Env = currentEnv()) {
  const explicit =
    envString(env, "VITE_TENX_AUTH_URL") ||
    envString(env, "VITE_SMARTWORK_API_BASE_URL") ||
    envString(env, "VITE_SMARTWORK_URL")
  if (explicit) return normalizeOpenAnalystTenxBaseUrl(explicit)
  const smartworkHost = smartworkHostedBaseUrl(env)
  if (smartworkHost) return smartworkHost
  return isProductionHost(env) ? PRODUCTION_TENX_BASE_URL : STAGING_TENX_BASE_URL
}

export function openAnalystAccountBaseUrl(env: Env = currentEnv()) {
  const explicit = envString(env, "VITE_OPENANALYST_ACCOUNT_URL")
  if (explicit) return normalizeOpenAnalystTenxBaseUrl(explicit)
  return isProductionHost(env) ? PRODUCTION_ACCOUNT_BASE_URL : STAGING_ACCOUNT_BASE_URL
}

export function openAnalystBillingBaseUrl(env: Env = currentEnv()) {
  const explicit = envString(env, "VITE_BILLING_URL")
  if (explicit) return normalizeOpenAnalystTenxBaseUrl(explicit)
  return usesProductionBillingHost(env) ? PRODUCTION_BILLING_BASE_URL : STAGING_BILLING_BASE_URL
}

export function openAnalystMarketplaceBaseUrl(env: Env = currentEnv()) {
  const explicit = envString(env, "VITE_MARKETPLACE_URL")
  if (explicit) {
    const base = normalizeOpenAnalystTenxBaseUrl(explicit)
    return base === PRODUCTION_TENX_BASE_URL ? `${base}/integrations` : base
  }
  return `${openAnalystBillingBaseUrl(env)}/integrations`
}

export function openAnalystBillingApiBaseUrl(env: Env = currentEnv()) {
  const base = openAnalystBillingBaseUrl(env)
  return `${base}${urlHost(base) === "ai.openanalyst.com" ? "/v2" : "/api"}`
}

export function openAnalystBillingApiV1BaseUrl(env: Env = currentEnv()) {
  return `${openAnalystBillingBaseUrl(env)}/api/v1`
}

export {
  PRODUCTION_ACCOUNT_BASE_URL,
  PRODUCTION_BILLING_BASE_URL,
  PRODUCTION_TENX_BASE_URL,
  PRODUCTION_WEB_BASE_URL,
  STAGING_ACCOUNT_BASE_URL,
  STAGING_BILLING_BASE_URL,
  STAGING_TENX_BASE_URL,
  STAGING_WEB_BASE_URL,
}
